import type { Feature, FeatureCollection, MultiPolygon, Polygon, Position } from "geojson";
import type { SamplePointCollection } from "@domain/Geo/sample";
import { Geo } from "@rogoag/geo";

export const MIN_SAFE_DISTANCE_METERS = 5;

/**
 * True when every position in the polygon is a finite [lng, lat] pair.
 *
 * The WASM boundary deserializes the whole Feature before it does any work, so a
 * single `null` in a ring makes it throw ("Expected a floating-point value, but
 * got None") rather than return a verdict. Integration sources produce such
 * rings — the same class of degenerate boundary as the empty MultiPolygon
 * handled by `geometryHasCoordinates`.
 */
function polygonHasFinitePositions(geom: Polygon | MultiPolygon): boolean {
	const coordSets = geom.type === "Polygon" ? [geom.coordinates] : geom.coordinates;
	for (const polygon of coordSets) {
		for (const ring of polygon) {
			for (const coord of ring) {
				if (!Number.isFinite(coord?.[0]) || !Number.isFinite(coord?.[1])) return false;
			}
		}
	}
	return true;
}

/**
 * Check if a point is inside a polygon AND at least `safeDistanceMeters`
 * away from the nearest boundary edge. Handles Polygon and MultiPolygon.
 *
 * Anything the WASM boundary can't evaluate — a malformed point, a malformed
 * polygon — counts as unsafe rather than throwing. This runs inside a `useMemo`
 * in JobMap with no local error boundary, so a throw here takes down the whole
 * dashboard (PORTAL-WEB-WW for the point side, PORTAL-WEB-X1 for the polygon).
 */
export function isPointSafeInsidePolygon(
	coord: Position,
	poly: Feature<Polygon | MultiPolygon>,
	safeDistanceMeters = MIN_SAFE_DISTANCE_METERS,
): boolean {
	// Malformed/missing coordinates (e.g. from a bad upload) can't be verified safe.
	if (!Number.isFinite(coord[0]) || !Number.isFinite(coord[1])) return false;
	try {
		// coord is [lon, lat] per GeoJSON spec
		return Geo.isPointSafeInsidePolygon(poly, coord[1], coord[0], safeDistanceMeters);
	} catch {
		// Backstop for any geometry the WASM parser rejects. `findUnsafeSampleIds`
		// screens the common case up front; this catches whatever it doesn't model.
		return false;
	}
}

/**
 * Find sample IDs that are NOT safe inside the boundary.
 * A point is unsafe if it's outside the boundary or within `safeDistanceMeters` of the edge.
 */
export function findUnsafeSampleIds(
	points: SamplePointCollection,
	boundary: Feature<Polygon | MultiPolygon>,
	safeDistanceMeters = MIN_SAFE_DISTANCE_METERS,
): Set<string> {
	const unsafe = new Set<string>();
	// Screen the boundary once. A boundary with a non-finite position makes every
	// point unverifiable, and rediscovering that per point would pay a WASM throw
	// each time.
	const boundaryIsUsable = polygonHasFinitePositions(boundary.geometry);
	for (const f of points.features) {
		if (f.geometry.type !== "Point") continue;
		if (!boundaryIsUsable || !isPointSafeInsidePolygon(f.geometry.coordinates, boundary, safeDistanceMeters)) {
			unsafe.add(f.properties.sample_id);
		}
	}
	return unsafe;
}

/**
 * Find sample IDs that are NOT safe inside any boundary feature in the FC.
 * A point is safe if it's safe in ANY boundary feature (union semantics).
 */
export function findUnsafeSampleIdsInFC(
	points: SamplePointCollection,
	boundaryFC: FeatureCollection,
	safeDistanceMeters = MIN_SAFE_DISTANCE_METERS,
): Set<string> {
	const boundaryFeatures = boundaryFC.features.filter(
		(f) => f.geometry.type === "Polygon" || f.geometry.type === "MultiPolygon",
	) as Feature<Polygon | MultiPolygon>[];
	if (boundaryFeatures.length === 0) return new Set();

	let result: Set<string> | null = null;
	for (const bf of boundaryFeatures) {
		const unsafeInThis = findUnsafeSampleIds(points, bf, safeDistanceMeters);
		if (!result) {
			result = unsafeInThis;
		} else {
			// Intersect: keep only IDs unsafe in ALL boundary features
			for (const id of result) {
				if (!unsafeInThis.has(id)) result.delete(id);
			}
		}
	}
	return result ?? new Set();
}
